/**
 * Import Modal - Hỗ trợ nhiều format
 * Cho phép import markers hoặc shapes từ file KMZ, Excel hoặc paste dữ liệu trực tiếp
 *
 * Hỗ trợ 3 chế độ:
 * 1. Upload file KMZ (Google Earth/Maps) - Khuyến nghị
 * 2. Upload file Excel (.xlsx, .xls, .csv)
 * 3. Paste dữ liệu dạng text (tab/space separated)
 *
 * KMZ format: Chứa file doc.kml với các Placemark có name và coordinates
 * - Point → Import thành Markers
 * - Polygon/LineString → Import thành Shapes (polygon/polyline) hoặc Markers (tâm)
 *
 * Excel format: Cột Tên, Giá, Tọa độ (lat, lng)
 * Paste format: STT | Tên | Giá | Tọa độ (lat, lng)
 */
import { useState, useRef } from 'react'
import {
  HiOutlineUpload,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineDocumentDownload,
  HiOutlineTrash,
  HiOutlineClipboardList,
  HiOutlinePencil,
  HiOutlineDocument,
  HiOutlineGlobeAlt,
  HiOutlineLocationMarker,
  HiOutlineCollection,
} from 'react-icons/hi'
import clsx from 'clsx'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'

// Mapping các tên cột có thể có trong Excel
const COLUMN_MAPPINGS = {
  name: ['tên', 'ten', 'name', 'tên chung cư', 'ten chung cu', 'tên dự án', 'ten du an', 'dự án', 'du an', 'chung cư', 'chung cu'],
  price_text: ['giá', 'gia', 'price', 'giá bán', 'gia ban', 'giá thuê', 'gia thue'],
  latitude: ['latitude', 'lat', 'vĩ độ', 'vi do'],
  longitude: ['longitude', 'lng', 'long', 'kinh độ', 'kinh do'],
  coordinates: ['tọa độ', 'toa do', 'coordinates', 'coord', 'location', 'vị trí', 'vi tri'], // Cột tọa độ gộp
  address: ['địa chỉ', 'dia chi', 'address', 'đường', 'duong'],
  area_text: ['diện tích', 'dien tich', 'area', 'm2'],
}

// Tìm cột phù hợp từ header
const findColumnIndex = (headers, field) => {
  const mappings = COLUMN_MAPPINGS[field] || []
  const lowerHeaders = headers.map(h => (h || '').toString().toLowerCase().trim())

  for (const mapping of mappings) {
    const index = lowerHeaders.findIndex(h => h.includes(mapping))
    if (index !== -1) return index
  }
  return -1
}

// Parse giá trị tọa độ
const parseCoordinate = (value) => {
  if (!value) return null
  if (typeof value === 'number') return value

  const str = value.toString().trim()
  const num = parseFloat(str.replace(',', '.'))
  return isNaN(num) ? null : num
}

// Parse tọa độ từ string "16.068224, 108.235559"
const parseCoordinateString = (coordStr) => {
  if (!coordStr) return { lat: null, lng: null }

  const str = coordStr.toString().trim()
  // Hỗ trợ các định dạng: "16.068, 108.235" hoặc "16.068 108.235"
  const parts = str.split(/[,\s]+/).filter(Boolean)

  if (parts.length >= 2) {
    const lat = parseFloat(parts[0])
    const lng = parseFloat(parts[1])
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng }
    }
  }
  return { lat: null, lng: null }
}

// Parse KMZ file (ZIP containing KML)
const parseKMZFile = async (file) => {
  const zip = new JSZip()
  const zipContent = await zip.loadAsync(file)

  // Find doc.kml or any .kml file
  let kmlContent = null
  for (const filename of Object.keys(zipContent.files)) {
    if (filename.endsWith('.kml')) {
      kmlContent = await zipContent.files[filename].async('string')
      break
    }
  }

  if (!kmlContent) {
    throw new Error('Không tìm thấy file KML trong KMZ')
  }

  return parseKMLContent(kmlContent)
}

// Parse single KML coordinate string "lng,lat,alt" -> { lat, lng }
const parseKMLCoordinate = (coordStr) => {
  const parts = coordStr.split(',').map(s => parseFloat(s.trim()))
  if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return { lng: parts[0], lat: parts[1] }
  }
  return null
}

// Parse multiple coordinates string "lng1,lat1,alt lng2,lat2,alt ..." -> array of { lat, lng }
const parseMultipleKMLCoordinates = (coordStr) => {
  // Split by whitespace to get individual coordinate sets
  const coordSets = coordStr.trim().split(/\s+/).filter(s => s.length > 0)
  const coords = []

  for (const set of coordSets) {
    const coord = parseKMLCoordinate(set)
    if (coord) {
      coords.push(coord)
    }
  }

  return coords
}

// Calculate centroid (center point) of a polygon
const calculateCentroid = (coords) => {
  if (!coords || coords.length === 0) return null

  let sumLat = 0
  let sumLng = 0

  for (const coord of coords) {
    sumLat += coord.lat
    sumLng += coord.lng
  }

  return {
    lat: sumLat / coords.length,
    lng: sumLng / coords.length,
  }
}

// Parse KML color (aabbggrr format) to hex (#rrggbb)
const parseKMLColor = (kmlColor) => {
  if (!kmlColor || kmlColor.length !== 8) return null
  // KML format: aabbggrr → reverse to rrggbb
  const r = kmlColor.substring(6, 8)
  const g = kmlColor.substring(4, 6)
  const b = kmlColor.substring(2, 4)
  return `#${r}${g}${b}`
}

// Get style from KML document
const getPlacemarkStyle = (placemark, xmlDoc) => {
  const defaultStyle = {
    strokeColor: '#3388ff',
    fillColor: '#3388ff',
    strokeWidth: 2,
    fillOpacity: 0.3,
  }

  // Get styleUrl reference
  const styleUrlEl = placemark.getElementsByTagName('styleUrl')[0]
  if (!styleUrlEl) return defaultStyle

  const styleUrl = styleUrlEl.textContent.trim()
  const styleId = styleUrl.replace('#', '')

  // Find Style or StyleMap with this ID
  const styles = xmlDoc.getElementsByTagName('Style')
  const styleMaps = xmlDoc.getElementsByTagName('StyleMap')

  // Check StyleMap first (may reference another style)
  for (let i = 0; i < styleMaps.length; i++) {
    if (styleMaps[i].getAttribute('id') === styleId) {
      // Get normal style from StyleMap
      const pairs = styleMaps[i].getElementsByTagName('Pair')
      for (let j = 0; j < pairs.length; j++) {
        const key = pairs[j].getElementsByTagName('key')[0]?.textContent
        if (key === 'normal') {
          const normalStyleUrl = pairs[j].getElementsByTagName('styleUrl')[0]?.textContent
          if (normalStyleUrl) {
            const normalId = normalStyleUrl.replace('#', '')
            // Find this style
            for (let k = 0; k < styles.length; k++) {
              if (styles[k].getAttribute('id') === normalId) {
                return extractStyleProperties(styles[k], defaultStyle)
              }
            }
          }
        }
      }
    }
  }

  // Check direct Style
  for (let i = 0; i < styles.length; i++) {
    if (styles[i].getAttribute('id') === styleId) {
      return extractStyleProperties(styles[i], defaultStyle)
    }
  }

  return defaultStyle
}

// Extract style properties from Style element
const extractStyleProperties = (styleEl, defaultStyle) => {
  const result = { ...defaultStyle }

  // LineStyle
  const lineStyle = styleEl.getElementsByTagName('LineStyle')[0]
  if (lineStyle) {
    const color = lineStyle.getElementsByTagName('color')[0]?.textContent
    if (color) {
      const hex = parseKMLColor(color)
      if (hex) result.strokeColor = hex
    }
    const width = lineStyle.getElementsByTagName('width')[0]?.textContent
    if (width) result.strokeWidth = parseInt(width) || 2
  }

  // PolyStyle
  const polyStyle = styleEl.getElementsByTagName('PolyStyle')[0]
  if (polyStyle) {
    const color = polyStyle.getElementsByTagName('color')[0]?.textContent
    if (color) {
      const hex = parseKMLColor(color)
      if (hex) result.fillColor = hex
      // Extract alpha for opacity (first 2 chars)
      const alpha = parseInt(color.substring(0, 2), 16)
      result.fillOpacity = Math.round((alpha / 255) * 100) / 100
    }
  }

  return result
}

// Convert coordinates to GeoJSON format
const coordinatesToGeoJSON = (coords, geometryType) => {
  if (geometryType === 'Polygon') {
    // GeoJSON Polygon: [[[lng, lat], [lng, lat], ...]]
    const ring = coords.map(c => [c.lng, c.lat])
    // Ensure polygon is closed
    if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
      ring.push(ring[0])
    }
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ring]
      },
      properties: {}
    }
  } else if (geometryType === 'LineString') {
    // GeoJSON LineString: [[lng, lat], [lng, lat], ...]
    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coords.map(c => [c.lng, c.lat])
      },
      properties: {}
    }
  }
  return null
}

// Detect geometry type in a Placemark
const getPlacemarkGeometry = (placemark) => {
  // Check for Point
  const point = placemark.getElementsByTagName('Point')[0]
  if (point) {
    const coordEl = point.getElementsByTagName('coordinates')[0]
    const coordStr = coordEl?.textContent?.trim()
    if (coordStr) {
      const coord = parseKMLCoordinate(coordStr)
      if (coord) {
        return { type: 'Point', center: coord, coordinates: [coord] }
      }
    }
  }

  // Check for Polygon
  const polygon = placemark.getElementsByTagName('Polygon')[0]
  if (polygon) {
    // Get outer boundary coordinates
    const outerBoundary = polygon.getElementsByTagName('outerBoundaryIs')[0]
    const linearRing = outerBoundary?.getElementsByTagName('LinearRing')[0]
    const coordEl = linearRing?.getElementsByTagName('coordinates')[0]
    const coordStr = coordEl?.textContent?.trim()

    if (coordStr) {
      const coords = parseMultipleKMLCoordinates(coordStr)
      if (coords.length > 0) {
        const center = calculateCentroid(coords)
        return { type: 'Polygon', center, coordinates: coords }
      }
    }
  }

  // Check for LineString
  const lineString = placemark.getElementsByTagName('LineString')[0]
  if (lineString) {
    const coordEl = lineString.getElementsByTagName('coordinates')[0]
    const coordStr = coordEl?.textContent?.trim()

    if (coordStr) {
      const coords = parseMultipleKMLCoordinates(coordStr)
      if (coords.length > 0) {
        const center = calculateCentroid(coords)
        return { type: 'LineString', center, coordinates: coords }
      }
    }
  }

  // Fallback: try to find any coordinates element
  const coordEl = placemark.getElementsByTagName('coordinates')[0]
  const coordStr = coordEl?.textContent?.trim()
  if (coordStr) {
    // Check if single or multiple coordinates
    if (coordStr.includes(' ')) {
      const coords = parseMultipleKMLCoordinates(coordStr)
      if (coords.length > 0) {
        const center = calculateCentroid(coords)
        return { type: 'Unknown', center, coordinates: coords }
      }
    } else {
      const coord = parseKMLCoordinate(coordStr)
      if (coord) {
        return { type: 'Point', center: coord, coordinates: [coord] }
      }
    }
  }

  return null
}

// Parse KML XML content
const parseKMLContent = (kmlString) => {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(kmlString, 'text/xml')

  const placemarks = xmlDoc.getElementsByTagName('Placemark')
  const parsedData = []
  const errors = []

  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i]
    const rowNum = i + 1

    // Get name
    const nameEl = placemark.getElementsByTagName('name')[0]
    const name = nameEl?.textContent?.trim() || ''

    // Get description
    const descEl = placemark.getElementsByTagName('description')[0]
    const description = descEl?.textContent?.trim() || ''

    if (!name) {
      errors.push(`Placemark ${rowNum}: Thiếu tên`)
      continue
    }

    // Get geometry (Point, Polygon, LineString)
    const geometry = getPlacemarkGeometry(placemark)

    if (!geometry || !geometry.center) {
      errors.push(`Placemark ${rowNum} "${name}": Không tìm thấy tọa độ`)
      continue
    }

    const { lat, lng } = geometry.center

    // Validate coordinates range
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      errors.push(`Placemark ${rowNum} "${name}": Tọa độ nằm ngoài phạm vi hợp lệ`)
      continue
    }

    // Get style for this placemark
    const style = getPlacemarkStyle(placemark, xmlDoc)

    // Generate GeoJSON for shapes
    let geojson = null
    if (geometry.type === 'Polygon' || geometry.type === 'LineString') {
      geojson = coordinatesToGeoJSON(geometry.coordinates, geometry.type)
    }

    // Try to extract price from name (e.g., "Sun Costa - 52-70tr/m2")
    let priceText = ''
    const priceMatch = name.match(/[-–]\s*(\d+[-–]?\d*\s*(tr|triệu|tỷ|ty)?[\/]?m2?)/i)
    if (priceMatch) {
      priceText = priceMatch[1].trim()
    }

    // Clean name (remove price part if exists)
    let cleanName = name
    if (priceMatch) {
      cleanName = name.replace(/\s*[-–]\s*\d+[-–]?\d*\s*(tr|triệu|tỷ|ty)?[\/]?m2?/i, '').trim()
    }

    parsedData.push({
      _rowNum: rowNum,
      _geometryType: geometry.type,
      _coordinates: geometry.coordinates, // Store full coordinates for potential shape import
      _geojson: geojson, // Store GeoJSON for shape import
      _style: style, // Store extracted KML style
      _description: description, // Store description
      name: cleanName || name,
      latitude: lat,
      longitude: lng,
      price_text: priceText,
      address: '',
      area_text: '',
      marker_color: style.fillColor || '#EF4444',
      is_valid: true,
    })
  }

  // Add summary of geometry types found
  const pointCount = parsedData.filter(d => d._geometryType === 'Point').length
  const polygonCount = parsedData.filter(d => d._geometryType === 'Polygon').length
  const lineCount = parsedData.filter(d => d._geometryType === 'LineString').length

  if (polygonCount > 0 || lineCount > 0) {
    const summary = []
    if (pointCount > 0) summary.push(`${pointCount} Point`)
    if (polygonCount > 0) summary.push(`${polygonCount} Polygon`)
    if (lineCount > 0) summary.push(`${lineCount} LineString`)
    errors.unshift(`📍 Đã tìm thấy: ${summary.join(', ')}`)
  }

  return { data: parsedData, errors }
}

// Parse dữ liệu paste từ text
const parsePastedData = (text) => {
  const lines = text.trim().split('\n').filter(line => line.trim())
  const parsedData = []
  const errors = []

  lines.forEach((line, index) => {
    const rowNum = index + 1

    // Split by tab hoặc nhiều spaces (2+)
    const parts = line.split(/\t|  +/).map(p => p.trim()).filter(Boolean)

    if (parts.length < 3) {
      errors.push(`Dòng ${rowNum}: Thiếu dữ liệu (cần ít nhất: STT/Tên, Giá, Tọa độ)`)
      return
    }

    // Detect format:
    // Format 1: STT | Tên | Giá | Tọa độ (4 cột)
    // Format 2: Tên | Giá | Tọa độ (3 cột, không có STT)

    let name, priceText, coordStr

    // Kiểm tra xem cột đầu có phải là số (STT) không
    const firstIsNumber = /^\d+$/.test(parts[0])

    if (firstIsNumber && parts.length >= 4) {
      // Format: STT | Tên | Giá | Tọa độ
      name = parts[1]
      priceText = parts[2]
      // Tọa độ có thể ở cột 3 và 4 riêng biệt hoặc gộp chung
      if (parts.length === 4) {
        coordStr = parts[3]
      } else {
        // Lat và Lng có thể tách riêng
        coordStr = `${parts[3]}, ${parts[4] || ''}`
      }
    } else if (firstIsNumber && parts.length === 3) {
      // Format: STT | Tên | Tọa độ (không có giá)
      name = parts[1]
      priceText = ''
      coordStr = parts[2]
    } else {
      // Format: Tên | Giá | Tọa độ (không có STT)
      name = parts[0]
      priceText = parts[1]
      coordStr = parts.length > 2 ? parts.slice(2).join(', ') : parts[2]
    }

    // Parse tọa độ
    const { lat, lng } = parseCoordinateString(coordStr)

    if (!name) {
      errors.push(`Dòng ${rowNum}: Thiếu tên`)
      return
    }

    if (lat === null || lng === null) {
      errors.push(`Dòng ${rowNum}: Tọa độ không hợp lệ "${coordStr}"`)
      return
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      errors.push(`Dòng ${rowNum}: Tọa độ nằm ngoài phạm vi hợp lệ`)
      return
    }

    parsedData.push({
      _rowNum: rowNum,
      name: name.trim(),
      latitude: lat,
      longitude: lng,
      price_text: priceText.trim() || '',
      address: '',
      area_text: '',
      marker_color: '#EF4444',
      is_valid: true,
    })
  })

  return { data: parsedData, errors }
}

function ExcelImportModal({ isOpen, onClose, onImport, onImportShapes, layers = [] }) {
  const [mode, setMode] = useState('select') // 'select' | 'paste' | 'excel' | 'kmz' | 'preview'
  const [file, setFile] = useState(null)
  const [pasteText, setPasteText] = useState('')
  const [previewData, setPreviewData] = useState([])
  const [errors, setErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [editingRow, setEditingRow] = useState(null)
  const fileInputRef = useRef(null)

  // Import type for KMZ with shapes: 'markers' | 'shapes' | 'both'
  const [importType, setImportType] = useState('markers')
  // Selected layer for shape import (null = create new layer)
  const [selectedLayerId, setSelectedLayerId] = useState(null)
  // New layer name when creating new layer
  const [newLayerName, setNewLayerName] = useState('')

  // Check if data has shapes (polygons/linestrings)
  const hasShapes = previewData.some(d => d._geometryType === 'Polygon' || d._geometryType === 'LineString')
  const polygonCount = previewData.filter(d => d._geometryType === 'Polygon').length
  const lineCount = previewData.filter(d => d._geometryType === 'LineString').length
  const pointCount = previewData.filter(d => d._geometryType === 'Point').length

  const resetState = () => {
    setMode('select')
    setFile(null)
    setPasteText('')
    setPreviewData([])
    setErrors([])
    setEditingRow(null)
    setImportType('markers')
    setSelectedLayerId(null)
    setNewLayerName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  // ==================== PASTE MODE ====================
  const handlePasteSubmit = () => {
    if (!pasteText.trim()) {
      setErrors(['Vui lòng dán dữ liệu vào ô nhập'])
      return
    }

    setIsLoading(true)
    setErrors([])

    try {
      const { data, errors: parseErrors } = parsePastedData(pasteText)
      setPreviewData(data)
      setErrors(parseErrors)
      setMode('preview')
    } catch (error) {
      console.error('Parse error:', error)
      setErrors(['Không thể đọc dữ liệu. Vui lòng kiểm tra định dạng.'])
    } finally {
      setIsLoading(false)
    }
  }

  // ==================== KMZ MODE ====================
  const handleKMZSelect = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    const ext = selectedFile.name.split('.').pop().toLowerCase()
    if (ext !== 'kmz' && ext !== 'kml') {
      setErrors(['File không hợp lệ. Vui lòng chọn file KMZ hoặc KML.'])
      return
    }

    setFile(selectedFile)
    setIsLoading(true)
    setErrors([])

    try {
      let result
      if (ext === 'kmz') {
        result = await parseKMZFile(selectedFile)
      } else {
        // KML file - read as text
        const text = await selectedFile.text()
        result = parseKMLContent(text)
      }
      setPreviewData(result.data)
      setErrors(result.errors)
      setMode('preview')
    } catch (error) {
      console.error('Error reading KMZ/KML:', error)
      setErrors([`Không thể đọc file: ${error.message}`])
    } finally {
      setIsLoading(false)
    }
  }

  // ==================== EXCEL MODE ====================
  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    const ext = selectedFile.name.split('.').pop().toLowerCase()
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setErrors(['File không hợp lệ. Vui lòng chọn file Excel (.xlsx, .xls) hoặc CSV.'])
      return
    }

    setFile(selectedFile)
    setIsLoading(true)
    setErrors([])

    try {
      const data = await readExcelFile(selectedFile)
      processExcelData(data)
      setMode('preview')
    } catch (error) {
      console.error('Error reading file:', error)
      setErrors(['Không thể đọc file. Vui lòng kiểm tra định dạng.'])
    } finally {
      setIsLoading(false)
    }
  }

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const sheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
          resolve(jsonData)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  const processExcelData = (rawData) => {
    if (!rawData || rawData.length < 1) {
      setErrors(['File không có dữ liệu.'])
      return
    }

    const headers = rawData[0]
    const dataRows = rawData.slice(1).filter(row => row.some(cell => cell !== ''))

    // Nếu không có data rows, thử parse như không có header
    if (dataRows.length === 0 && rawData.length > 0) {
      // Có thể file không có header, thử parse trực tiếp
      processExcelWithoutHeader(rawData)
      return
    }

    const mapping = {
      name: findColumnIndex(headers, 'name'),
      price_text: findColumnIndex(headers, 'price_text'),
      latitude: findColumnIndex(headers, 'latitude'),
      longitude: findColumnIndex(headers, 'longitude'),
      coordinates: findColumnIndex(headers, 'coordinates'), // Cột tọa độ gộp
      address: findColumnIndex(headers, 'address'),
      area_text: findColumnIndex(headers, 'area_text'),
    }

    // Kiểm tra: cần có (name) VÀ (lat+lng HOẶC coordinates)
    const hasName = mapping.name !== -1
    const hasLatLng = mapping.latitude !== -1 && mapping.longitude !== -1
    const hasCoordinates = mapping.coordinates !== -1

    if (!hasName) {
      // Thử parse như không có header (auto-detect format)
      processExcelWithoutHeader(rawData)
      return
    }

    if (!hasLatLng && !hasCoordinates) {
      setErrors([
        'Không tìm thấy cột tọa độ.',
        'Cần có: (Latitude + Longitude) HOẶC (Tọa độ gộp dạng "16.068, 108.235")',
      ])
      return
    }

    const parsedData = []
    const rowErrors = []

    dataRows.forEach((row, index) => {
      const rowNum = index + 2

      const name = row[mapping.name]?.toString().trim()

      let lat = null
      let lng = null

      // Ưu tiên lat/lng riêng, nếu không có thì dùng cột coordinates gộp
      if (hasLatLng) {
        lat = parseCoordinate(row[mapping.latitude])
        lng = parseCoordinate(row[mapping.longitude])
      }

      // Nếu lat/lng null, thử parse từ cột coordinates gộp
      if ((lat === null || lng === null) && hasCoordinates) {
        const coordStr = row[mapping.coordinates]?.toString().trim()
        const parsed = parseCoordinateString(coordStr)
        lat = parsed.lat
        lng = parsed.lng
      }

      if (!name) {
        rowErrors.push(`Dòng ${rowNum}: Thiếu tên`)
        return
      }

      if (lat === null || lng === null) {
        rowErrors.push(`Dòng ${rowNum}: Tọa độ không hợp lệ`)
        return
      }

      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        rowErrors.push(`Dòng ${rowNum}: Tọa độ nằm ngoài phạm vi hợp lệ`)
        return
      }

      parsedData.push({
        _rowNum: rowNum,
        name,
        latitude: lat,
        longitude: lng,
        price_text: mapping.price_text !== -1 ? row[mapping.price_text]?.toString().trim() || '' : '',
        address: mapping.address !== -1 ? row[mapping.address]?.toString().trim() || '' : '',
        area_text: mapping.area_text !== -1 ? row[mapping.area_text]?.toString().trim() || '' : '',
        marker_color: '#EF4444',
        is_valid: true,
      })
    })

    setPreviewData(parsedData)
    setErrors(rowErrors)
  }

  // Parse Excel không có header (auto-detect format giống paste)
  const processExcelWithoutHeader = (rawData) => {
    const parsedData = []
    const rowErrors = []

    rawData.forEach((row, index) => {
      const rowNum = index + 1

      // Filter empty cells
      const cells = row.map(c => c?.toString().trim() || '').filter(Boolean)

      if (cells.length < 2) {
        rowErrors.push(`Dòng ${rowNum}: Thiếu dữ liệu`)
        return
      }

      let name, priceText = '', coordStr

      // Detect format based on first cell
      const firstIsNumber = /^\d+$/.test(cells[0])

      if (firstIsNumber && cells.length >= 4) {
        // Format: STT | Tên | Giá | Tọa độ
        name = cells[1]
        priceText = cells[2]
        coordStr = cells.slice(3).join(', ')
      } else if (firstIsNumber && cells.length === 3) {
        // Format: STT | Tên | Tọa độ
        name = cells[1]
        coordStr = cells[2]
      } else if (cells.length >= 3) {
        // Format: Tên | Giá | Tọa độ
        name = cells[0]
        priceText = cells[1]
        coordStr = cells.slice(2).join(', ')
      } else {
        // Format: Tên | Tọa độ
        name = cells[0]
        coordStr = cells[1]
      }

      const { lat, lng } = parseCoordinateString(coordStr)

      if (!name) {
        rowErrors.push(`Dòng ${rowNum}: Thiếu tên`)
        return
      }

      if (lat === null || lng === null) {
        rowErrors.push(`Dòng ${rowNum}: Tọa độ không hợp lệ "${coordStr}"`)
        return
      }

      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        rowErrors.push(`Dòng ${rowNum}: Tọa độ nằm ngoài phạm vi hợp lệ`)
        return
      }

      parsedData.push({
        _rowNum: rowNum,
        name: name.trim(),
        latitude: lat,
        longitude: lng,
        price_text: priceText.trim(),
        address: '',
        area_text: '',
        marker_color: '#EF4444',
        is_valid: true,
      })
    })

    setPreviewData(parsedData)
    setErrors(rowErrors)
  }

  // ==================== PREVIEW & EDIT ====================
  const handleRemoveRow = (index) => {
    setPreviewData(prev => prev.filter((_, i) => i !== index))
  }

  const handleEditRow = (index) => {
    setEditingRow(index)
  }

  const handleSaveEdit = (index, updatedRow) => {
    setPreviewData(prev => prev.map((row, i) => i === index ? { ...row, ...updatedRow } : row))
    setEditingRow(null)
  }

  const handleCancelEdit = () => {
    setEditingRow(null)
  }

  const handleImport = async () => {
    if (previewData.length === 0) return

    setImporting(true)
    try {
      // Separate points and shapes
      const pointsData = previewData.filter(d => d._geometryType === 'Point' || !d._geometryType)
      const shapesData = previewData.filter(d => d._geometryType === 'Polygon' || d._geometryType === 'LineString')

      // Import markers
      if (importType === 'markers' || importType === 'both') {
        // For 'markers' import all as markers (shapes use centroid as position)
        // For 'both' only import points as markers
        const dataToImport = importType === 'markers' ? previewData : pointsData
        if (dataToImport.length > 0) {
          const markers = dataToImport.map(({ _rowNum, _geometryType, _coordinates, _geojson, _style, _description, is_valid, ...marker }) => marker)
          await onImport(markers)
        }
      }

      // Import shapes
      if ((importType === 'shapes' || importType === 'both') && shapesData.length > 0 && onImportShapes) {
        const shapes = shapesData.map(d => ({
          name: d.name,
          description: d._description || '',
          shape_type: d._geometryType === 'Polygon' ? 'polygon' : 'polyline',
          geojson: d._geojson,
          stroke_color: d._style?.strokeColor || '#3388ff',
          fill_color: d._style?.fillColor || '#3388ff',
          stroke_width: d._style?.strokeWidth || 2,
          fill_opacity: d._style?.fillOpacity || 0.3,
          stroke_opacity: 1,
          is_visible: true,
          show_label: true,
        }))

        await onImportShapes(shapes, selectedLayerId, newLayerName || 'KMZ Import')
      }

      handleClose()
    } catch (error) {
      console.error('Import error:', error)
      setErrors(['Lỗi khi import. Vui lòng thử lại.'])
    } finally {
      setImporting(false)
    }
  }

  const downloadTemplate = () => {
    const template = [
      ['STT', 'Tên chung cư', 'Giá', 'Địa chỉ', 'Diện tích', 'Latitude', 'Longitude'],
      [1, 'Căn hộ ABC', '2.5 tỷ', '123 Nguyễn Văn Linh, Đà Nẵng', '75m²', '16.054407', '108.202167'],
      [2, 'Chung cư XYZ', '1.8 tỷ', '456 Lê Duẩn, Đà Nẵng', '65m²', '16.047079', '108.206230'],
    ]

    const ws = XLSX.utils.aoa_to_sheet(template)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Markers')
    ws['!cols'] = [
      { wch: 5 }, { wch: 25 }, { wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
    ]
    XLSX.writeFile(wb, 'markers_template.xlsx')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Import Markers</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'select' && 'Chọn cách nhập dữ liệu'}
              {mode === 'paste' && 'Dán dữ liệu từ Excel/Sheets'}
              {mode === 'excel' && 'Upload file Excel'}
              {mode === 'kmz' && 'Upload file KMZ/KML từ Google Earth'}
              {mode === 'preview' && `Xem trước ${previewData.length} markers`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Mode: Select */}
          {mode === 'select' && (
            <div className="space-y-4">
              {/* Option 1: Upload KMZ (Recommended) */}
              <button
                onClick={() => setMode('kmz')}
                className="w-full p-5 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <HiOutlineGlobeAlt className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Upload file KMZ/KML (Khuyến nghị)</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      File từ Google Earth/Maps có sẵn tọa độ chính xác
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Chính xác
                  </span>
                </div>
              </button>

              {/* Option 2: Paste */}
              <button
                onClick={() => setMode('paste')}
                className="w-full p-5 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <HiOutlineClipboardList className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Dán dữ liệu</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      Copy từ Excel/Google Sheets rồi dán vào đây
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    Nhanh
                  </span>
                </div>
              </button>

              {/* Option 3: Upload Excel */}
              <button
                onClick={() => setMode('excel')}
                className="w-full p-5 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <HiOutlineDocument className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Upload file Excel</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      Hỗ trợ .xlsx, .xls, .csv
                    </div>
                  </div>
                </div>
              </button>

              {/* Format guide */}
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Định dạng dữ liệu hỗ trợ</h4>
                <div className="text-sm text-gray-600 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">KMZ</span>
                    <span>File Google Earth với Placemarks (tên + tọa độ tự động)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">Excel</span>
                    <span>Cột: Tên*, Giá, Tọa độ* (lat, lng)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mode: Paste */}
          {mode === 'paste' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dán dữ liệu vào đây
                </label>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={`1\tHiyori Aqua\t80-90\t16.068224, 108.235559\n2\tSun Costa\t52-70\t16.070058, 108.242270\n...`}
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Mỗi dòng là 1 marker. Các cột cách nhau bằng Tab hoặc nhiều dấu cách.
                </p>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      {errors.map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setMode('select'); setErrors([]) }}
                  className="btn-outline"
                >
                  Quay lại
                </button>
                <button
                  onClick={handlePasteSubmit}
                  disabled={!pasteText.trim() || isLoading}
                  className="btn-primary flex-1"
                >
                  {isLoading ? (
                    <>
                      <span className="spinner w-4 h-4 mr-2" />
                      Đang xử lý...
                    </>
                  ) : (
                    <>
                      <HiOutlineCheck className="w-4 h-4 mr-1" />
                      Xem trước
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Mode: KMZ Upload */}
          {mode === 'kmz' && (
            <div className="space-y-4">
              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
                  'border-gray-300 hover:border-green-400 hover:bg-green-50'
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <HiOutlineGlobeAlt className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  Click để chọn file KMZ hoặc KML
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  File từ Google Earth/Maps
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".kmz,.kml"
                  onChange={handleKMZSelect}
                  className="hidden"
                />
              </div>

              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                  <span className="ml-3 text-gray-600">Đang đọc file...</span>
                </div>
              )}

              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">Cách tạo file KMZ</h4>
                <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                  <li>Mở Google Earth hoặc Google My Maps</li>
                  <li>Tạo các điểm đánh dấu (Placemark) cho mỗi vị trí</li>
                  <li>Đặt tên điểm kèm giá nếu có (VD: "Sun Costa - 52tr/m2")</li>
                  <li>Export/Download dưới dạng KMZ hoặc KML</li>
                </ol>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      {errors.map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => { setMode('select'); setErrors([]) }}
                className="btn-outline"
              >
                Quay lại
              </button>
            </div>
          )}

          {/* Mode: Excel Upload */}
          {mode === 'excel' && (
            <div className="space-y-4">
              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
                  'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <HiOutlineUpload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  Click để chọn file hoặc kéo thả vào đây
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Hỗ trợ: .xlsx, .xls, .csv
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div className="flex items-center justify-center">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  <HiOutlineDocumentDownload className="w-5 h-5" />
                  Tải file mẫu
                </button>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      {errors.map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => { setMode('select'); setErrors([]) }}
                className="btn-outline"
              >
                Quay lại
              </button>
            </div>
          )}

          {/* Mode: Preview */}
          {mode === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <HiOutlineCheck className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {previewData.length} items hợp lệ
                      {hasShapes && (
                        <span className="text-sm font-normal text-gray-600 ml-1">
                          ({pointCount > 0 ? `${pointCount} điểm` : ''}{polygonCount > 0 ? `${pointCount > 0 ? ', ' : ''}${polygonCount} polygon` : ''}{lineCount > 0 ? `${pointCount > 0 || polygonCount > 0 ? ', ' : ''}${lineCount} đường` : ''})
                        </span>
                      )}
                    </p>
                    {errors.length > 0 && (
                      <p className="text-sm text-amber-600">{errors.filter(e => !e.startsWith('📍')).length} dòng có lỗi (đã bỏ qua)</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMode('select')
                    setPreviewData([])
                    setErrors([])
                    setPasteText('')
                    setFile(null)
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Nhập lại
                </button>
              </div>

              {/* Import Type Selection - Only show when shapes are detected */}
              {hasShapes && onImportShapes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-3">Chọn cách import Polygon/Line</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100 cursor-pointer">
                      <input
                        type="radio"
                        name="importType"
                        value="markers"
                        checked={importType === 'markers'}
                        onChange={(e) => setImportType(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <HiOutlineLocationMarker className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="font-medium text-gray-900">Import tất cả thành Markers</span>
                        <p className="text-xs text-gray-500">Polygon/Line sẽ dùng tâm làm vị trí marker</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100 cursor-pointer">
                      <input
                        type="radio"
                        name="importType"
                        value="shapes"
                        checked={importType === 'shapes'}
                        onChange={(e) => setImportType(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <HiOutlineCollection className="w-5 h-5 text-purple-600" />
                      <div>
                        <span className="font-medium text-gray-900">Import Polygon/Line thành Shapes</span>
                        <p className="text-xs text-gray-500">Giữ nguyên hình dạng, hiển thị như vùng trên bản đồ</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100 cursor-pointer">
                      <input
                        type="radio"
                        name="importType"
                        value="both"
                        checked={importType === 'both'}
                        onChange={(e) => setImportType(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex gap-1">
                        <HiOutlineLocationMarker className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-400">+</span>
                        <HiOutlineCollection className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Import cả hai</span>
                        <p className="text-xs text-gray-500">Point → Markers, Polygon/Line → Shapes</p>
                      </div>
                    </label>
                  </div>

                  {/* Layer selection for shapes */}
                  {(importType === 'shapes' || importType === 'both') && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Thêm shapes vào layer
                      </label>
                      <select
                        value={selectedLayerId || ''}
                        onChange={(e) => setSelectedLayerId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">+ Tạo layer mới</option>
                        {layers.map(layer => (
                          <option key={layer.id} value={layer.id}>
                            {layer.name} ({layer.shape_count || 0} shapes)
                          </option>
                        ))}
                      </select>

                      {!selectedLayerId && (
                        <input
                          type="text"
                          value={newLayerName}
                          onChange={(e) => setNewLayerName(e.target.value)}
                          placeholder="Tên layer mới (VD: Quỹ Đất Huế)"
                          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Errors */}
              {errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <HiOutlineExclamation className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium mb-1">Một số dòng có lỗi:</p>
                      <ul className="list-disc list-inside space-y-0.5 max-h-20 overflow-y-auto">
                        {errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {errors.length > 5 && (
                          <li className="text-amber-600">...và {errors.length - 5} lỗi khác</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview table */}
              {previewData.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      Xem trước & Chỉnh sửa
                    </p>
                    <p className="text-xs text-gray-500">
                      Click vào dòng để sửa
                    </p>
                  </div>
                  <div className="overflow-x-auto max-h-72">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-600 w-8">#</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600 min-w-[150px]">Tên</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600 min-w-[100px]">Giá</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600 min-w-[180px]">Tọa độ</th>
                          <th className="px-3 py-2 w-20"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {previewData.map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {editingRow === index ? (
                              // Edit mode
                              <EditableRow
                                row={row}
                                index={index}
                                onSave={handleSaveEdit}
                                onCancel={handleCancelEdit}
                              />
                            ) : (
                              // View mode
                              <>
                                <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                                <td className="px-3 py-2 font-medium text-gray-900">
                                  <div className="flex items-center gap-2">
                                    {row.name}
                                    {row._geometryType === 'Polygon' && (
                                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Polygon</span>
                                    )}
                                    {row._geometryType === 'LineString' && (
                                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">Line</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-primary-600">
                                  {row.price_text || '-'}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-gray-500">
                                  {row.latitude.toFixed(6)}, {row.longitude.toFixed(6)}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleEditRow(index)}
                                      className="p-1 hover:bg-blue-100 rounded text-blue-500"
                                      title="Sửa"
                                    >
                                      <HiOutlinePencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleRemoveRow(index)}
                                      className="p-1 hover:bg-red-100 rounded text-red-500"
                                      title="Xóa"
                                    >
                                      <HiOutlineTrash className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {previewData.length === 0 && (
                <div className="py-8 text-center text-gray-500">
                  <p>Không có dữ liệu hợp lệ để import</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <p className="text-sm text-gray-500">
            {mode === 'preview' && previewData.length > 0 && (
              importType === 'markers'
                ? `Sẵn sàng import ${previewData.length} markers`
                : importType === 'shapes'
                  ? `Sẵn sàng import ${polygonCount + lineCount} shapes`
                  : `Sẵn sàng import ${pointCount} markers + ${polygonCount + lineCount} shapes`
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
            {mode === 'preview' && (
              <button
                onClick={handleImport}
                disabled={previewData.length === 0 || importing}
                className={clsx(
                  'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2',
                  previewData.length > 0 && !importing
                    ? 'bg-primary-600 hover:bg-primary-700'
                    : 'bg-gray-400 cursor-not-allowed'
                )}
              >
                {importing ? (
                  <>
                    <span className="spinner w-4 h-4" />
                    Đang import...
                  </>
                ) : (
                  <>
                    <HiOutlineCheck className="w-4 h-4" />
                    {importType === 'markers'
                      ? `Import ${previewData.length} markers`
                      : importType === 'shapes'
                        ? `Import ${polygonCount + lineCount} shapes`
                        : `Import tất cả`
                    }
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Editable Row Component
function EditableRow({ row, index, onSave, onCancel }) {
  const [editData, setEditData] = useState({
    name: row.name,
    price_text: row.price_text,
    latitude: row.latitude.toString(),
    longitude: row.longitude.toString(),
  })

  const handleSave = () => {
    const lat = parseFloat(editData.latitude)
    const lng = parseFloat(editData.longitude)

    if (!editData.name.trim()) {
      alert('Vui lòng nhập tên')
      return
    }
    if (isNaN(lat) || isNaN(lng)) {
      alert('Tọa độ không hợp lệ')
      return
    }

    onSave(index, {
      ...row,
      name: editData.name.trim(),
      price_text: editData.price_text.trim(),
      latitude: lat,
      longitude: lng,
    })
  }

  return (
    <>
      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
      <td className="px-2 py-1">
        <input
          type="text"
          value={editData.name}
          onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
          autoFocus
        />
      </td>
      <td className="px-2 py-1">
        <input
          type="text"
          value={editData.price_text}
          onChange={(e) => setEditData(prev => ({ ...prev, price_text: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
          placeholder="VD: 80-90"
        />
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1">
          <input
            type="text"
            value={editData.latitude}
            onChange={(e) => setEditData(prev => ({ ...prev, latitude: e.target.value }))}
            className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
            placeholder="Lat"
          />
          <input
            type="text"
            value={editData.longitude}
            onChange={(e) => setEditData(prev => ({ ...prev, longitude: e.target.value }))}
            className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
            placeholder="Lng"
          />
        </div>
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1">
          <button
            onClick={handleSave}
            className="p-1 bg-green-100 hover:bg-green-200 rounded text-green-600"
            title="Lưu"
          >
            <HiOutlineCheck className="w-4 h-4" />
          </button>
          <button
            onClick={onCancel}
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
            title="Hủy"
          >
            <HiOutlineX className="w-4 h-4" />
          </button>
        </div>
      </td>
    </>
  )
}

export default ExcelImportModal
